"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";
import { useLanguageContext } from "../providers/language-provider";
import type { FilterOptionPostingDate } from "../types/filtermenu";
import type { TranslationsMap } from "../types/language";
import { createPostingDateOptions } from "../utils/filter-helpers";
import { useFilterOptions } from "./use-filter-options";

/**
 * Hook for managing the posting date filter logic.
 * Returns the current postingDateFilter and a setter function.
 */
export function usePostingDateFilter() {
  const $ = _c(21);
  const text = useLanguageContext() as TranslationsMap;
  const filterInfo = useFilterOptions();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  let t0;
  bb0: {
    if (filterInfo) {
      let t1;
      if ($[0] !== filterInfo.postingDateCounts || $[1] !== text) {
        t1 = createPostingDateOptions(filterInfo.postingDateCounts, text);
        $[0] = filterInfo.postingDateCounts;
        $[1] = text;
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      t0 = t1;
      break bb0;
    }
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = [];
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    t0 = t1;
  }
  const postingDateOptions = t0;
  let t1;
  bb1: {
    if (!filterInfo) {
      t1 = undefined;
      break bb1;
    }
    let t2;
    if ($[4] !== searchParams) {
      t2 = searchParams.get("postingDate");
      $[4] = searchParams;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const postingDate = t2;
    let t3;
    if ($[6] !== searchParams) {
      t3 = searchParams.get("time");
      $[6] = searchParams;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const time = t3;
    if (!postingDate && time) {
      t1 = `data.postingDate_timestamp >= ${time}`;
      break bb1;
    }
    let t4;
    if ($[8] !== postingDate || $[9] !== postingDateOptions) {
      let t5;
      if ($[11] !== postingDate) {
        t5 = o => o.label === postingDate;
        $[11] = postingDate;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      t4 = postingDateOptions.find(t5);
      $[8] = postingDate;
      $[9] = postingDateOptions;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    const option = t4;
    if (option) {
      t1 = option.value;
      break bb1;
    }
    t1 = undefined;
  }
  const postingDateFilter = t1;
  let t2;
  if ($[13] !== pathname || $[14] !== postingDateOptions || $[15] !== router || $[16] !== searchParams) {
    t2 = postingDate_0 => {
      let option_0;
      if (postingDate_0) {
        option_0 = postingDateOptions.find(o_0 => o_0.value === postingDate_0);
        if (!option_0) {
          throw new Error(`Can not use non-existing postingDateFilter: ${postingDate_0}`);
        }
      }
      const params = new URLSearchParams(Array.from(searchParams.entries()));
      if (option_0?.label) {
        params.set("postingDate", option_0.label);
      } else {
        params.delete("postingDate");
      }
      router.push(`${pathname}?${params.toString()}`);
    };
    $[13] = pathname;
    $[14] = postingDateOptions;
    $[15] = router;
    $[16] = searchParams;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  const setPostingDateFilter = t2;
  let t3;
  if ($[18] !== postingDateFilter || $[19] !== setPostingDateFilter) {
    t3 = {
      postingDateFilter,
      setPostingDateFilter
    };
    $[18] = postingDateFilter;
    $[19] = setPostingDateFilter;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
}